import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { customerOrderService, finishedGoodsBalancesService } from '../services/api';

const CALC_FORM_LABELS = {
  form1: 'Форма 1',
  form2: 'Форма 2',
};

const PAYMENT_METHOD_LABELS = {
  deferred: 'Відтермінування',
  full: 'Повна оплата',
};

function pad2(n) {
  return String(n).padStart(2, '0');
}

function formatAmount(value) {
  if (value == null) return '—';
  return `${value.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} грн`;
}

function formatDateOnly(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('uk-UA');
}

function shipmentStatusForLine(line) {
  const qty = Number(line.quantity) || 0;
  const shipped = Number(line.shipped_quantity) || 0;
  if (qty > 0 && shipped >= qty) return 'Відвантажено';
  if (shipped > 0) return 'Частково відвантажено';
  return 'Не відвантажено';
}

function PositionsLogPage() {
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [stockByProductId, setStockByProductId] = useState({});
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const [ordersRes, balancesRes] = await Promise.all([
          customerOrderService.getAll(),
          finishedGoodsBalancesService.getBalances(),
        ]);
        if (cancelled) return;
        const list = Array.isArray(ordersRes.data?.results)
          ? ordersRes.data.results
          : Array.isArray(ordersRes.data)
          ? ordersRes.data
          : [];
        setOrders(list);
        const stockMap = {};
        (balancesRes.data?.by_product || []).forEach((r) => {
          stockMap[Number(r.product_id)] = Number(r.total_quantity) || 0;
        });
        setStockByProductId(stockMap);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const rows = useMemo(() => {
    const out = [];
    (orders || []).forEach((o) => {
      (o.lines || []).forEach((l) => {
        const qty = Number(l.quantity) || 0;
        const price = l.price != null && l.price !== '' ? Number(l.price) : null;
        const amount = price != null ? qty * price : null;
        const d = o.order_date ? new Date(o.order_date) : null;
        const stock = stockByProductId[Number(l.product)] ?? 0;
        out.push({
          key: `${o.id}-${l.id}`,
          orderNumber: o.number,
          customerName: o.customer_name,
          orderDate: o.order_date,
          deadline: o.deadline,
          productName: l.product_name,
          productArticle: l.product_article,
          quantity: qty,
          price,
          amount,
          calcForm: CALC_FORM_LABELS[l.calc_form] || '—',
          paymentMethod: PAYMENT_METHOD_LABELS[l.payment_method] || '—',
          productionStatus: l.production_status_display || '—',
          shipmentStatus: shipmentStatusForLine(l),
          stock,
          availability: qty > 0 && qty <= stock ? 'Є на складі' : 'Потрібне виробництво',
          month: d ? `${d.getFullYear()}-${pad2(d.getMonth() + 1)}` : '—',
          quarter: d ? `Q${Math.floor(d.getMonth() / 3) + 1}` : '—',
          year: d ? d.getFullYear() : '—',
          comment: o.comment || '',
        });
      });
    });
    return out.sort((a, b) => new Date(b.orderDate || 0) - new Date(a.orderDate || 0));
  }, [orders, stockByProductId]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.orderNumber, r.customerName, r.productName, r.productArticle, r.comment]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q),
    );
  }, [rows, search]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Журнал позицій замовлень
        </Typography>
        <TextField
          size="small"
          placeholder="Пошук"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table stickyHeader size="small" sx={{ minWidth: 1900 }}>
          <TableHead>
            <TableRow>
              <TableCell>Номер замовлення</TableCell>
              <TableCell>Назва замовника</TableCell>
              <TableCell>Дата замовлення</TableCell>
              <TableCell>Бажаний дедлайн</TableCell>
              <TableCell>Назва виробу</TableCell>
              <TableCell>Артикул</TableCell>
              <TableCell align="right">Кількість штук</TableCell>
              <TableCell align="right">Ціна з ПДВ</TableCell>
              <TableCell align="right">Сума</TableCell>
              <TableCell>Вид розрахунку</TableCell>
              <TableCell>Метод оплати</TableCell>
              <TableCell>Статус виробництва</TableCell>
              <TableCell>Статус відвантаження</TableCell>
              <TableCell align="right">Залишок ГП</TableCell>
              <TableCell>Перевірка наявності</TableCell>
              <TableCell>Місяць</TableCell>
              <TableCell>Квартал</TableCell>
              <TableCell>Рік</TableCell>
              <TableCell>Коментар</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={19} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>
                    Немає позицій
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredRows.map((r) => (
                <TableRow key={r.key} hover>
                  <TableCell>{r.orderNumber}</TableCell>
                  <TableCell>{r.customerName}</TableCell>
                  <TableCell>{formatDateOnly(r.orderDate)}</TableCell>
                  <TableCell>{formatDateOnly(r.deadline)}</TableCell>
                  <TableCell>{r.productName}</TableCell>
                  <TableCell>{r.productArticle || '—'}</TableCell>
                  <TableCell align="right">{r.quantity}</TableCell>
                  <TableCell align="right">{formatAmount(r.price)}</TableCell>
                  <TableCell align="right">{formatAmount(r.amount)}</TableCell>
                  <TableCell>{r.calcForm}</TableCell>
                  <TableCell>{r.paymentMethod}</TableCell>
                  <TableCell>{r.productionStatus}</TableCell>
                  <TableCell>{r.shipmentStatus}</TableCell>
                  <TableCell align="right">{r.stock}</TableCell>
                  <TableCell
                    sx={{
                      color: r.availability === 'Є на складі' ? 'success.dark' : 'warning.dark',
                      fontWeight: 600,
                    }}
                  >
                    {r.availability}
                  </TableCell>
                  <TableCell>{r.month}</TableCell>
                  <TableCell>{r.quarter}</TableCell>
                  <TableCell>{r.year}</TableCell>
                  <TableCell>{r.comment || '—'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default PositionsLogPage;
